<div class="min-h-screen bg-white">
  <!-- ═══════════════════════ Header ═══════════════════════ -->
  <header class="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
    <div class="max-w-7xl mx-auto px-4 py-3 flex items-center">
      <!-- Mobile menu toggle -->
      <button type="button" (click)="mobileMenuOpen = !mobileMenuOpen"
        class="md:hidden mr-3 p-1 text-gray-600 hover:text-gray-900">
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      <div class="flex items-center gap-3">
        <img src="assets/icons/logo-v2.svg" alt="Westhetics" class="h-6 max-w-[160px] object-contain" />
        <div class="h-5 w-px bg-gray-300 mx-1"></div>
        <span class="text-sm text-gray-600">Partner API</span>
        <span class="px-2 py-0.5 text-[10px] font-semibold bg-indigo-100 text-indigo-700 rounded-full uppercase tracking-wider">v1</span>
      </div>
    </div>
  </header>

  <div class="max-w-7xl xl:max-w-[1600px] mx-auto flex relative">
    <!-- ══════════ Mobile overlay ══════════ -->
    <div *ngIf="mobileMenuOpen" (click)="mobileMenuOpen = false"
      class="fixed inset-0 bg-black/30 z-30 md:hidden"></div>

    <!-- ═══════════════════════ Sidebar ═══════════════════════ -->
    <nav
      class="fixed md:sticky top-0 md:top-[57px] left-0 z-40 md:z-auto w-72 md:w-64 h-screen md:h-[calc(100vh-57px)] overflow-y-auto bg-white border-r border-gray-200 py-6 px-3 transition-transform duration-200 md:translate-x-0"
      [class.-translate-x-full]="!mobileMenuOpen"
      [class.translate-x-0]="mobileMenuOpen">

      <!-- Intro sections -->
      <div class="space-y-0.5 mb-4">
        <button type="button" *ngFor="let section of [
          { id: 'getting-started', label: 'Começando' },
          { id: 'authentication', label: 'Authentication' },
          { id: 'rate-limiting', label: 'Rate Limiting' },
          { id: 'pagination', label: 'Pagination' },
          { id: 'errors', label: 'Error Handling' }
        ]"
          (click)="scrollToSection(section.id)"
          class="w-full text-left px-3 py-1.5 text-sm rounded transition-colors"
          [ngClass]="activeSection === section.id
            ? 'bg-indigo-50 text-indigo-700 font-medium'
            : 'text-gray-600 hover:text-indigo-700 hover:bg-gray-50'">
          {{ section.label }}
        </button>
      </div>

      <!-- API endpoint groups -->
      <div class="border-t border-gray-200 pt-4 space-y-4">
        <div *ngFor="let group of apiSpec">
          <div class="px-3 text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
            {{ group.title }}
          </div>
          <div class="space-y-0.5">
            <button type="button" *ngFor="let ep of group.endpoints"
              (click)="scrollToSection(ep.id)"
              class="w-full flex items-center gap-2 px-3 py-1.5 text-sm rounded transition-colors"
              [ngClass]="activeSection === ep.id
                ? 'bg-indigo-50 text-indigo-700 font-medium'
                : 'text-gray-600 hover:text-indigo-700 hover:bg-gray-50'">
              <span class="text-[10px] font-bold px-1.5 py-0.5 rounded leading-none"
                [ngClass]="{
                  'bg-emerald-100 text-emerald-700': ep.method === 'GET',
                  'bg-blue-100 text-blue-700': ep.method === 'POST'
                }">
                {{ ep.method }}
              </span>
              <span class="font-mono text-xs truncate">{{ getShortPath(ep.path) }}</span>
            </button>
          </div>
        </div>
      </div>
    </nav>

    <!-- ═══════════════════════ Content ═══════════════════════ -->
    <main class="flex-1 px-4 md:px-8 py-8 min-w-0 max-w-4xl">

      <!-- ──── Getting Started (Portuguese) ──── -->
      <section id="getting-started" data-section class="mb-12 scroll-mt-20">
        <h2 class="text-2xl font-bold text-gray-900 mb-4">Começando</h2>
        <div class="text-sm text-gray-600 space-y-4 leading-relaxed">
          <p>
            Bem-vindo à documentação da <strong class="text-gray-900">Partner API da Westhetics</strong>.
            Esta API permite que aplicações parceiras acessem dados de pacientes, anamneses,
            planos de tratamento e vendas de forma segura e padronizada.
          </p>

          <h3 class="text-base font-semibold text-gray-800 !mt-6">Como funciona</h3>
          <ol class="list-decimal pl-5 space-y-2">
            <li>
              Solicite suas credenciais
              (<code class="bg-gray-100 px-1.5 py-0.5 rounded text-xs text-indigo-700 font-mono">app_key</code>
              e
              <code class="bg-gray-100 px-1.5 py-0.5 rounded text-xs text-indigo-700 font-mono">app_secret</code>)
              ao administrador da clínica.
            </li>
            <li>Gere um token de acesso usando o endpoint de autenticação abaixo.</li>
            <li>
              Inclua o token no header
              <code class="bg-gray-100 px-1.5 py-0.5 rounded text-xs text-indigo-700 font-mono">Authorization: Bearer TOKEN</code>
              em todas as requisições.
            </li>
            <li>O token é válido por <strong class="text-gray-900">60 minutos</strong>. Após expirar, gere um novo.</li>
          </ol>

          <h3 class="text-base font-semibold text-gray-800 !mt-6">URL Base</h3>
          <pre class="code-block !py-2.5 !px-4"><code>{{ apiBaseUrl }}</code></pre>

          <h3 class="text-base font-semibold text-gray-800 !mt-6">Escopos Disponíveis</h3>
          <p>Cada aplicação recebe permissões específicas (scopes) definidas pelo administrador:</p>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div *ngFor="let scope of [
              { code: 'patients:read', label: 'Acesso a pacientes' },
              { code: 'anamnesis:read', label: 'Acesso a anamneses' },
              { code: 'treatment_plans:read', label: 'Acesso a planos de tratamento' },
              { code: 'sales:read', label: 'Acesso a vendas' }
            ]"
              class="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100">
              <code class="text-xs text-indigo-700 font-mono font-medium">{{ scope.code }}</code>
              <span class="text-xs text-gray-500">— {{ scope.label }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- ──── Authentication Panel ──── -->
      <section id="authentication" data-section class="mb-12 scroll-mt-20">
        <app-auth-panel [hasToken]="!!token" (tryItClick)="onTryAuth()"></app-auth-panel>
      </section>

      <!-- ──── Rate Limiting ──── -->
      <section id="rate-limiting" data-section class="mb-12 scroll-mt-20">
        <h2 class="text-2xl font-bold text-gray-900 mb-4">Rate Limiting</h2>
        <div class="text-sm text-gray-600 space-y-4 leading-relaxed">
          <p>
            All endpoints are rate-limited to <strong class="text-gray-900">100 requests per 60 seconds</strong>
            per application. Rate limit information is included in response headers:
          </p>
          <div class="overflow-x-auto">
            <table class="w-full text-sm border border-gray-200 rounded-lg">
              <thead>
                <tr class="bg-gray-50">
                  <th class="px-4 py-2.5 text-left font-medium text-gray-600">Header</th>
                  <th class="px-4 py-2.5 text-left font-medium text-gray-600">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr class="border-t border-gray-100">
                  <td class="px-4 py-2 font-mono text-xs text-indigo-700">X-RateLimit-Limit</td>
                  <td class="px-4 py-2 text-gray-600">Maximum number of requests allowed per window.</td>
                </tr>
                <tr class="border-t border-gray-100">
                  <td class="px-4 py-2 font-mono text-xs text-indigo-700">X-RateLimit-Remaining</td>
                  <td class="px-4 py-2 text-gray-600">Requests remaining in the current window.</td>
                </tr>
                <tr class="border-t border-gray-100">
                  <td class="px-4 py-2 font-mono text-xs text-indigo-700">X-RateLimit-Reset</td>
                  <td class="px-4 py-2 text-gray-600">Seconds until the rate limit window resets.</td>
                </tr>
                <tr class="border-t border-gray-100">
                  <td class="px-4 py-2 font-mono text-xs text-indigo-700">Retry-After</td>
                  <td class="px-4 py-2 text-gray-600">Seconds to wait before retrying (only on 429 responses).</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            When the limit is exceeded, the API returns
            <code class="bg-gray-100 px-1.5 py-0.5 rounded text-xs text-red-600 font-mono">429 Too Many Requests</code>.
          </p>
        </div>
      </section>

      <!-- ──── Pagination ──── -->
      <section id="pagination" data-section class="mb-12 scroll-mt-20">
        <h2 class="text-2xl font-bold text-gray-900 mb-4">Pagination</h2>
        <div class="text-sm text-gray-600 space-y-4 leading-relaxed">
          <p>All list endpoints return paginated responses with the following structure:</p>
          <pre class="code-block"><code [innerHTML]="paginationExample | jsonHighlight"></code></pre>
          <ul class="list-disc pl-5 space-y-1.5">
            <li>
              <code class="bg-gray-100 px-1.5 py-0.5 rounded text-xs text-indigo-700 font-mono">current_page</code>
              — Zero-based page number.
            </li>
            <li>
              <code class="bg-gray-100 px-1.5 py-0.5 rounded text-xs text-indigo-700 font-mono">total_pages</code>
              — Total number of pages available.
            </li>
            <li>
              <code class="bg-gray-100 px-1.5 py-0.5 rounded text-xs text-indigo-700 font-mono">total_elements</code>
              — Total number of items across all pages.
            </li>
            <li>
              <code class="bg-gray-100 px-1.5 py-0.5 rounded text-xs text-indigo-700 font-mono">elements_per_page</code>
              — Number of items per page (maximum 100).
            </li>
          </ul>
        </div>
      </section>

      <!-- ──── Errors ──── -->
      <section id="errors" data-section class="mb-12 scroll-mt-20">
        <h2 class="text-2xl font-bold text-gray-900 mb-4">Error Handling</h2>
        <div class="text-sm text-gray-600 space-y-4 leading-relaxed">
          <p>All error responses follow a consistent JSON structure:</p>
          <pre class="code-block"><code [innerHTML]="errorExample | jsonHighlight"></code></pre>

          <h3 class="text-base font-semibold text-gray-800 !mt-6">Common Status Codes</h3>
          <div class="overflow-x-auto">
            <table class="w-full text-sm border border-gray-200 rounded-lg">
              <thead>
                <tr class="bg-gray-50">
                  <th class="px-4 py-2.5 text-left font-medium text-gray-600 w-24">Status</th>
                  <th class="px-4 py-2.5 text-left font-medium text-gray-600">Meaning</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let err of [
                  { status: 400, cls: 'bg-orange-100 text-orange-700', desc: 'Bad Request — Invalid parameters or request body.' },
                  { status: 401, cls: 'bg-orange-100 text-orange-700', desc: 'Unauthorized — Missing or invalid access token.' },
                  { status: 403, cls: 'bg-orange-100 text-orange-700', desc: 'Forbidden — Insufficient scopes for this endpoint.' },
                  { status: 404, cls: 'bg-orange-100 text-orange-700', desc: 'Not Found — Requested resource does not exist.' },
                  { status: 429, cls: 'bg-red-100 text-red-700', desc: 'Too Many Requests — Rate limit exceeded. Respect the Retry-After header.' },
                  { status: 500, cls: 'bg-red-100 text-red-700', desc: 'Internal Server Error — An unexpected error occurred on the server.' }
                ]" class="border-t border-gray-100">
                  <td class="px-4 py-2">
                    <span class="px-2 py-0.5 text-xs font-bold rounded" [ngClass]="err.cls">{{ err.status }}</span>
                  </td>
                  <td class="px-4 py-2 text-gray-600">{{ err.desc }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- ═══════════════════════ API Endpoints ═══════════════════════ -->
      <div *ngFor="let group of apiSpec" class="mb-12">
        <div [id]="group.id" data-section class="scroll-mt-20 mb-6">
          <h2 class="text-2xl font-bold text-gray-900 mb-1">{{ group.title }}</h2>
          <p class="text-sm text-gray-600">{{ group.description }}</p>
        </div>

        <app-endpoint
          *ngFor="let ep of group.endpoints"
          [endpoint]="ep"
          (tryItClick)="onTryEndpoint($event)">
        </app-endpoint>
      </div>

      <!-- Footer -->
      <div class="border-t border-gray-200 pt-8 pb-4 text-center text-xs text-gray-400">
        Westhetics Partner API v1 — &copy; {{ currentYear }} Westhetics. All rights reserved.
      </div>
    </main>

    <!-- ═══════════════════════ Right Panel - Try It ═══════════════════════ -->
    <!-- Mobile overlay -->
    <div *ngIf="tryItPanelOpen" (click)="closeTryItPanel()"
      class="fixed inset-0 bg-black/30 z-40 xl:hidden"></div>

    <aside
      class="fixed right-0 top-0 h-full z-50 w-[380px] max-w-[calc(100vw-3rem)] shadow-xl transition-transform duration-300 xl:sticky xl:top-[57px] xl:right-auto xl:h-[calc(100vh-57px)] xl:z-auto xl:max-w-none xl:shadow-none xl:translate-x-0 xl:flex-shrink-0 border-l border-gray-200 bg-white"
      [class.translate-x-full]="!tryItPanelOpen">
      <app-try-it-panel
        [apiBaseUrl]="apiBaseUrl"
        [activeEndpoint]="activeEndpoint"
        (tokenChange)="onTokenChange($event)"
        (closePanel)="closeTryItPanel()">
      </app-try-it-panel>
    </aside>
  </div>
</div>
